import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, ArrowRight, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO, { breadcrumbJsonLd } from "@/components/SEO";
import { MODELS, BRAND } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CatalogPage() {
    const [email, setEmail] = useState("");
    const [downloading, setDownloading] = useState(false);

    useEffect(() => window.scrollTo(0, 0), []);

    const downloadPdf = async (logEmail = null) => {
        try {
            setDownloading(true);
            // log lead if email provided
            if (logEmail) {
                await axios.post(`${API}/catalog/lead`, { email: logEmail }).catch(() => null);
            }
            const url = `${API}/katalog`;
            const a = document.createElement("a");
            a.href = url;
            a.download = "alu-ograde-katalog.pdf";
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Katalog se preuzima...");
        } catch (e) {
            console.error(e);
            toast.error("Greška pri preuzimanju kataloga.");
        } finally {
            setDownloading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return toast.error("Unesite email adresu.");
        await downloadPdf(email);
        setEmail("");
    };

    const jsonLd = breadcrumbJsonLd([
        { name: "Početna", path: "/" },
        { name: "Katalog", path: "/katalog" },
    ]);

    return (
        <>
            <SEO
                title="Katalog (PDF) — Aluminijske ograde"
                description="Preuzmite besplatan katalog aluminijskih ograda alu-ograde.ba u PDF formatu. Sve modele (LUNAR, STELLAR, ASTRAL, ECLIPSE, ORBIT, GALACTIC), tehničke specifikacije i 217 RAL boja na jednom mjestu."
                path="/katalog"
                keywords="katalog aluminijskih ograda, pdf katalog, alu ograde katalog, ograde BiH cjenovnik"
                jsonLd={jsonLd}
            />

            <section className="pt-[120px] pb-12 lg:pt-[160px] lg:pb-16 border-b border-[hsl(var(--line))]">
                <div className="container-p">
                    <nav className="flex items-center gap-2 text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-ink))]" aria-label="Breadcrumbs">
                        <Link to="/" className="hover:text-[hsl(var(--accent))]">Početna</Link>
                        <span>/</span>
                        <span className="text-[hsl(var(--ink))]">Katalog</span>
                    </nav>

                    <div className="mt-10 grid items-start gap-14 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <span className="eyebrow">PDF Katalog · besplatan</span>
                            <h1 className="mt-6 font-[Cabinet_Grotesk] text-[clamp(2.6rem,7vw,5.4rem)] font-black leading-[0.95] uppercase">
                                Katalog <br />
                                <span className="text-[hsl(var(--accent))]">2026.</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-[1.02rem] leading-[1.7] text-[hsl(var(--muted-ink))]">
                                Kompletan pregled naše ponude aluminijskih ograda — svih 6 modela, tehničke specifikacije, RAL boje i kontakt podaci. Idealno za printanje i konsultaciju sa arhitektom ili izvođačem.
                            </p>

                            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {[
                                    "6 modela sa specifikacijama",
                                    "8 najpopularnijih RAL boja",
                                    "Tehničke dimenzije i visine",
                                    "Kontakt i podaci o garanciji",
                                ].map((b) => (
                                    <li key={b} className="flex items-start gap-2.5">
                                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--accent))]" strokeWidth={2.5} />
                                        <span className="text-[0.94rem] text-[hsl(var(--ink))]">{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-10 flex flex-wrap gap-3">
                                <button
                                    onClick={() => downloadPdf()}
                                    disabled={downloading}
                                    className="btn btn-accent disabled:opacity-60"
                                    data-testid="catalog-direct-download"
                                >
                                    {downloading ? "Priprema..." : "Preuzmi PDF"} <Download className="h-4 w-4" />
                                </button>
                                <Link to="/#modeli" className="btn btn-ghost">
                                    Pogledaj modele online
                                </Link>
                            </div>

                            <div className="mt-12 border-t border-[hsl(var(--line))] pt-10">
                                <span className="eyebrow">Pošalji mi katalog</span>
                                <h2 className="mt-4 font-[Cabinet_Grotesk] text-[1.6rem] font-extrabold leading-tight">
                                    Email mi katalog za kasnije.
                                </h2>
                                <p className="mt-3 max-w-md text-[0.94rem] text-[hsl(var(--muted-ink))]">
                                    Ostavite email — preuzmite katalog odmah, a mi ćemo ga zapamtiti za vas i kasnije.
                                </p>
                                <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row" data-testid="catalog-email-form">
                                    <input
                                        type="email"
                                        required
                                        placeholder="vasa@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="flex-1 border border-[hsl(var(--line))] bg-white px-4 py-3 text-[0.95rem] focus:border-[hsl(var(--ink))] focus:outline-none focus:shadow-[0_0_0_3px_rgba(230,57,70,0.18)]"
                                        data-testid="catalog-email-input"
                                    />
                                    <button type="submit" className="btn btn-primary" disabled={downloading} data-testid="catalog-email-submit">
                                        <Mail className="h-4 w-4" /> Pošalji & preuzmi
                                    </button>
                                </form>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative">
                                <span className="corner-tl" aria-hidden />
                                <span className="corner-br" aria-hidden />
                                <div className="bg-[hsl(var(--paper-2))] p-10 aspect-[3/4] flex flex-col">
                                    <div className="flex items-center gap-2.5">
                                        <span className="grid h-9 w-9 place-items-center bg-[hsl(var(--accent))] text-white">
                                            <span className="font-[Cabinet_Grotesk] text-[15px] font-extrabold">AO</span>
                                        </span>
                                        <span className="font-[Cabinet_Grotesk] text-base font-extrabold uppercase">
                                            alu-ograde<span className="text-[hsl(var(--accent))]">.ba</span>
                                        </span>
                                    </div>
                                    <div className="mt-auto">
                                        <span className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[hsl(var(--muted-ink))]">PDF · 2026</span>
                                        <h3 className="mt-3 font-[Cabinet_Grotesk] text-[2.2rem] font-black leading-[0.95] uppercase">
                                            Aluminijske <br />
                                            ograde <br />
                                            <span className="text-[hsl(var(--accent))]">po mjeri.</span>
                                        </h3>
                                        <div className="mt-6 flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em]">
                                            <FileText className="h-4 w-4 text-[hsl(var(--accent))]" />
                                            6 modela · 217 RAL boja
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p className="mt-4 text-center text-[0.78rem] text-[hsl(var(--muted-ink))]">PDF · približno 200–400 KB · format A4</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* List of models inside catalog */}
            <section className="py-20 lg:py-28 bg-[hsl(var(--paper-2))]">
                <div className="container-p">
                    <div className="reveal">
                        <span className="eyebrow">Modeli u katalogu</span>
                        <h2 className="mt-6 text-[clamp(1.8rem,3.6vw,2.8rem)] leading-[1]">
                            Šta je <span className="text-[hsl(var(--accent))]">unutra.</span>
                        </h2>
                    </div>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {MODELS.map((m) => (
                            <Link to={`/modeli/${m.id}`} key={m.id} className="group flex items-center gap-4 border border-[hsl(var(--line))] bg-white p-5 transition-colors hover:border-[hsl(var(--ink))]">
                                <span className="font-[Cabinet_Grotesk] text-[0.78rem] font-bold tracking-[0.16em] text-[hsl(var(--muted-ink))]">{m.n}</span>
                                <div className="flex-1">
                                    <div className="font-[Cabinet_Grotesk] text-lg font-extrabold uppercase">{m.name}</div>
                                    <div className="text-[0.8rem] text-[hsl(var(--accent))] font-semibold uppercase tracking-wider">{m.tag}</div>
                                </div>
                                <ArrowRight className="h-5 w-5 text-[hsl(var(--ink))] transition-transform group-hover:translate-x-1" />
                            </Link>
                        ))}
                    </div>
                    <p className="mt-10 text-sm text-[hsl(var(--muted-ink))]">
                        Trebate dodatne informacije? <a href={BRAND.phoneHref} className="font-bold text-[hsl(var(--accent))] hover:underline">Pozovite {BRAND.phone}</a> — odgovaramo Pon–Pet 08:00–17:00.
                    </p>
                </div>
            </section>
        </>
    );
}
